import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiHeart, FiShoppingCart, FiMinus, FiPlus, FiStar, FiPackage } from 'react-icons/fi';
import { useProducts } from '../hooks/useProducts';
import { useBrands } from '../hooks/useBrands';
import { useCart } from '../hooks/useCart';
import { useProductOptions, useProductVariants, findVariantByOptions } from '../hooks/useProductOptions';
import { useProductCustomFields } from '../hooks/useProductCustomFields';
import { useVariantLinks } from '../hooks/useVariantLinks';
import { useProductRatings, useCanRateProduct, useOrderItemRating } from '../hooks/useProductRatings';
import { useAuthContext } from '../hooks/useAuthContext';
import { useLanguage } from '../context/LanguageContext';
import RatingModal from '../components/RatingModal';
import LoginModal from '../components/LoginModal';
import { slugify } from '../utils/slugify';
import { useFavorites } from '../hooks/useFavorites'; // <-- added
import { supabase } from '../config/supabase';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 20px;
`;

const ProductContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  margin-bottom: 64px;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const ImageSection = styled.div`
  min-width: 0;
`;

const MainImage = styled.img`
  width: 100%;
  height: 500px;
  object-fit: cover;
  border-radius: 12px;
  background: #f9fafb;

  @media (max-width: 968px) {
    height: min(62vw, 360px);
    object-fit: contain;
    object-position: center;
    background: #f3f4f6;
  }
`;

const Thumbnails = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
  max-width: 100%;
  overflow: hidden;

  @media (max-width: 968px) {
    flex-wrap: nowrap;
    max-width: none;
    overflow: visible;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scroll-snap-type: x proximity;
    touch-action: pan-x;
    padding-bottom: 6px;

    &::-webkit-scrollbar {
      height: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 999px;
    }
  }
`;

const Thumbnail = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  border: 2px solid ${props => props.active ? '#34D399' : '#e5e7eb'};
  transition: all 0.2s;

  &:hover {
    border-color: #34D399;
  }

  @media (max-width: 968px) {
    flex: 0 0 72px;
    width: 72px;
    height: 72px;
    scroll-snap-align: start;
  }
`;

const DetailsSection = styled.div``;

const SellerText = styled.div`
  font-size: 14px;
  color: #9ca3af;
  margin-top: 24px;
  font-weight: 400;
`;

const SellerLink = styled(Link)`
  color: #6b7280;
  font-weight: 500;
  text-decoration: none;
  transition: color 0.2s;
  
  &:hover {
    color: #5efba9;
  }
`;

const CategoryTag = styled(Link)`
  font-size: 12px;
  font-weight: 500;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 16px;
  text-decoration: none;
  display: inline-block;
  transition: color 0.2s;
  cursor: pointer;
  
  &:hover {
    color: #5efba9;
  }
`;

const Title = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 32px;
  color: #000000;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: -0.5px;
  
  @media (max-width: 768px) {
    font-size: 32px;
    margin-bottom: 24px;
  }
`;

const Rating = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const Stars = styled.div`
  color: #fbbf24;
`;

const RatingText = styled.span`
  color: #6b7280;
  font-size: 14px;
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
`;

const CurrentPrice = styled.div`
  font-size: 42px;
  font-weight: 700;
  color: #000000;
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const OriginalPrice = styled.div`
  font-size: 20px;
  color: #9ca3af;
  text-decoration: line-through;
`;

const Discount = styled.div`
  background: #dc2626;
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
`;

const StockBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #d1fae5;
  color: #065f46;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
`;

const Variants = styled.div`
  margin: 32px 0;
`;

const VariantLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
`;

const VariantOptions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const VariantButton = styled.button`
  padding: 10px 20px;
  border: 2px solid ${props => props.selected ? '#34D399' : '#e5e7eb'};
  background: ${props => props.selected ? '#d1fae5' : 'white'};
  color: ${props => props.selected ? '#065f46' : '#374151'};
  border-radius: 8px;
  font-weight: ${props => props.selected ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s;
  position: relative;

  &:hover:not(:disabled) {
    border-color: #34D399;
    background: ${props => props.selected ? '#d1fae5' : '#f0fdf4'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    text-decoration: line-through;
  }
`;

const CustomFieldSection = styled.div`
  margin: 32px 0;
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;
`;

const CustomFieldItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

const CustomFieldLabel = styled.span`
  font-weight: 600;
  color: #374151;
  text-transform: capitalize;
`;

const CustomFieldValue = styled.span`
  color: #6b7280;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin: 32px 0;
`;

const NoteInput = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  margin: 24px 0;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #34D399;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const NoteLabel = styled.label`
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
  display: block;
`;

const QuantityWrapper = styled.div`
  display: flex;
  align-items: center;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const QuantityButton = styled.button`
  width: 44px;
  height: 44px;
  border: none;
  background: #f9fafb;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Quantity = styled.span`
  padding: 0 24px;
  min-width: 60px;
  text-align: center;
  font-size: 16px;
  font-weight: 500;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 16px;
  margin: 32px 0;
`;

const PrimaryButton = styled.button`
  flex: 1;
  padding: 16px;
  background: #34D399;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #10b981;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #9ca3af;
  }
`;

const SecondaryButton = styled.button`
  padding: 16px 24px;
  border: 2px solid #e5e7eb;
  background: white;
  color: #374151;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    border-color: #34D399;
    color: #34D399;
  }
`;

const Description = styled.div`
  margin-top: 64px;
`;

const DescriptionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
`;

const DescriptionText = styled.p`
  color: #6b7280;
  line-height: 1.6;
`;

const RatingsSection = styled.div`
  margin-top: 64px;
  padding-top: 32px;
  border-top: 2px solid #e5e7eb;
`;

const RatingsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const RatingsTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
`;

const AverageRating = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AverageStars = styled.div`
  display: flex;
  gap: 4px;
  color: #fbbf24;
  font-size: 24px;
`;

const AverageNumber = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
`;

const RatingCount = styled.span`
  font-size: 14px;
  color: #6b7280;
`;

const RatingButton = styled.button`
  padding: 10px 20px;
  border: 1px solid #5efba9;
  background: #5efba9;
  color: #1f2937;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    background: #4de89e;
  }
`;

const RatingItem = styled.div`
  padding: 20px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  margin-bottom: 16px;
  background: white;
`;

const RatingItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const ReviewerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReviewerName = styled.div`
  font-weight: 600;
  color: #1f2937;
`;

const ReviewDate = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const RatingStars = styled.div`
  display: flex;
  gap: 2px;
  color: #fbbf24;
  font-size: 18px;
`;

const RatingBody = styled.p`
  color: #4b5563;
  line-height: 1.6;
  margin: 0;
  font-style: italic;
`;

const EmptyRatings = styled.div`
  text-align: center;
  padding: 40px;
  color: #6b7280;
`;

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { products, loading: productsLoading } = useProducts();
  const { brands } = useBrands();
  const { addToCart } = useCart();
  const { isArabic } = useLanguage();
  const { session } = useAuthContext();
  const { isFavorite, addFavorite, removeFavorite } = useFavorites(); // <-- existing
  const [wishlistAdding, setWishlistAdding] = useState(false); // <-- existing

  // --- Add toast state + helpers ---
  const [toasts, setToasts] = useState([]);
  const showToast = (message, type = 'success') => {
    const tid = Date.now() + Math.random();
    setToasts((s) => [...s, { id: tid, message, type }]);
    setTimeout(() => setToasts((s) => s.filter(t => t.id !== tid)), 3500);
  };
  const removeToast = (id) => setToasts((s) => s.filter(t => t.id !== id));
  // --- end toast helpers ---

  // Parse product ID (could be UUID or numeric string)
  const productId = useMemo(() => {
    // If it's a numeric string, try to find by parseInt
    // Otherwise use as-is (UUID)
    const parsed = /^\d+$/.test(id) ? parseInt(id) : id;
    return parsed;
  }, [id]);

  const { optionGroups, loading: optionsLoading } = useProductOptions(productId);
  const { variants, loading: variantsLoading } = useProductVariants(productId);
  const { customFields } = useProductCustomFields(productId);
  const { links: variantLinks, loading: linksLoading } = useVariantLinks(productId);
  const { ratings, loading: ratingsLoading, refetch: refetchRatings } = useProductRatings(productId);

  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({}); // { groupId: valueId }
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [note, setNote] = useState('');
  const [customFieldValues, setCustomFieldValues] = useState({}); // { fieldId: value }

  // Find the product from the database
  const dbProduct = useMemo(() => {
    // Try to find by UUID first, then by numeric ID
    return products.find(p => p.id === productId || p.id === id || String(p.id) === String(id));
  }, [products, productId, id]);

  // Check if user is admin/superadmin
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!session) {
        setIsAdmin(false);
        return;
      }
      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('is_admin, is_superadmin')
          .eq('id', session.user.id)
          .single();
        
        if (!error && profile) {
          setIsAdmin(profile.is_admin === true || profile.is_superadmin === true);
        }
      } catch (err) {
        console.error('Error checking admin status:', err);
        setIsAdmin(false);
      }
    };
    checkAdminStatus();
  }, [session]);

  // Check if product is draft - redirect if so (only for non-admin users)
  useEffect(() => {
    if (dbProduct && dbProduct.status !== 'active' && !isAdmin) {
      // Product is draft and user is not admin - redirect to home
      navigate('/');
    }
  }, [dbProduct, isAdmin, navigate]);

  // Find the brand for this product
  const brand = dbProduct ? brands.find(b => b.id === dbProduct.brand_id) : null;

  // Debug logging
  console.log('ProductDetail Debug:', {
    productId: id,
    foundProduct: dbProduct,
    brand: brand,
    allProducts: products.length
  });

  // Resolve selected variant
  const selectedVariant = useMemo(() => {
    if (variants.length === 0 || Object.keys(selectedOptions).length === 0) {
      return null;
    }
    const found = findVariantByOptions(variants, selectedOptions);
    if (!found && optionGroups.length > 0) {
      // Debug: log why variant wasn't found
      console.log('Variant not found for selected options:', {
        selectedOptions,
        variants: variants.map(v => ({
          id: v.id,
          options: v.variant_options?.map(vo => ({ group_id: vo.group_id, value_id: vo.value_id }))
        })),
        optionGroups: optionGroups.map(g => ({ id: g.id, name: g.name, label: g.label }))
      });
    }
    return found;
  }, [variants, selectedOptions, optionGroups]);

  // Check if user can rate this product
  const selectedVariantForRating = useMemo(() => {
    return selectedVariant ? selectedVariant.id : null;
  }, [selectedVariant]);

  const { canRate, orderItems, loading: canRateLoading } = useCanRateProduct(productId, selectedVariantForRating);

  // Find the order item that matches this product/variant for rating
  const matchingOrderItem = useMemo(() => {
    if (!canRate || !orderItems.length) return null;

    // For variant products, find order item with matching variant_id
    if (selectedVariantForRating) {
      return orderItems.find(item => item.variant_id === selectedVariantForRating) || orderItems[0];
    }

    // For non-variant products, find any order item
    return orderItems[0];
  }, [canRate, orderItems, selectedVariantForRating]);

  // Find the order for this order item
  const matchingOrder = useMemo(() => {
    if (!matchingOrderItem) return null;
    // Order data is embedded in matchingOrderItem from useCanRateProduct query.
    return matchingOrderItem.order || null;
  }, [matchingOrderItem]);

  // Get existing rating for the order item
  const { rating: existingRating } = useOrderItemRating(matchingOrderItem?.id);

  // Get current user to show their pending ratings too
  const { session: authSession } = useAuthContext();

  // Filter approved ratings (and pending from current user for immediate feedback)
  const approvedRatings = useMemo(() => {
    if (!ratings || ratings.length === 0) {
      console.log('ProductDetail - No ratings found:', { ratings, productId });
      return [];
    }


    const filtered = ratings.filter(r => {
      // Show if approved OR if it's the current user's rating (regardless of status)
      const isApproved = r.status === 'approved';
      const isUserRating = r.created_by === authSession?.user?.id;
      const shouldShow = isApproved || isUserRating;

      if (!shouldShow) {
        console.log('ProductDetail - Rating filtered out:', {
          id: r.id,
          status: r.status,
          created_by: r.created_by,
          current_user: authSession?.user?.id,
          product_id: r.product_id,
          variant_id: r.variant_id,
          isApproved,
          isUserRating
        });
      }

      return shouldShow;
    });

    console.log('ProductDetail - Ratings filtered:', {
      total: ratings.length,
      approved: filtered.length,
      productId,
      currentUserId: authSession?.user?.id,
      ratings: ratings.map(r => ({
        id: r.id,
        status: r.status,
        stars: r.stars,
        created_by: r.created_by,
        isCurrentUser: r.created_by === authSession?.user?.id
      }))
    });

    return filtered;
  }, [ratings, authSession, productId]);

  // Calculate average rating (only from approved ratings, excluding pending)
  const averageRating = useMemo(() => {
    const onlyApproved = approvedRatings.filter(r => r.status === 'approved');
    if (onlyApproved.length === 0) return 0;
    const sum = onlyApproved.reduce((acc, r) => acc + (r.stars || 0), 0);
    return sum / onlyApproved.length;
  }, [approvedRatings]);

  // Calculate price - use variant price if selected, otherwise product price
  const basePrice = selectedVariant ? selectedVariant.price : (dbProduct?.price || 0);
  const discount = dbProduct?.dis && dbProduct.dis !== 'no' ? parseFloat(dbProduct.dis) : null;
  const originalPrice = basePrice;
  const currentPrice = discount ? (originalPrice * (1 - discount / 100)) : originalPrice;

  // Product images - prioritize selected variant images, then show all variant and product images
  const images = useMemo(() => {
    const allImages = [];
    const selectedVariantImages = [];

    // If a variant is selected, collect its images first
    if (selectedVariant) {
      // Add selected variant's single img first if available
      if (selectedVariant.img && selectedVariant.img.trim()) {
        selectedVariantImages.push(selectedVariant.img);
      }

      // Add selected variant's images array if available
      if (selectedVariant.images && Array.isArray(selectedVariant.images)) {
        selectedVariant.images.forEach(img => {
          if (img && img.trim() && !selectedVariantImages.includes(img)) {
            selectedVariantImages.push(img);
          }
        });
      }
    }

    // Add product images
    if (dbProduct?.img && dbProduct.img.trim()) {
      allImages.push(dbProduct.img);
    }
    if (dbProduct?.images && Array.isArray(dbProduct.images)) {
      dbProduct.images.forEach(img => {
        if (img && img.trim() && !allImages.includes(img)) {
          allImages.push(img);
        }
      });
    }

    // Add all variant images from all variants
    if (variants && Array.isArray(variants)) {
      variants.forEach(variant => {
        // Add variant's single img if available
        if (variant.img && variant.img.trim() && !allImages.includes(variant.img)) {
          allImages.push(variant.img);
        }

        // Add variant's images array if available
        if (variant.images && Array.isArray(variant.images)) {
          variant.images.forEach(img => {
            if (img && img.trim() && !allImages.includes(img)) {
              allImages.push(img);
            }
          });
        }
      });
    }

    // Combine: selected variant images first, then all other images
    const finalImages = [...selectedVariantImages];
    allImages.forEach(img => {
      if (!finalImages.includes(img)) {
        finalImages.push(img);
      }
    });

    return finalImages.length > 0 ? finalImages : ['https://via.placeholder.com/500x500'];
  }, [selectedVariant, variants, dbProduct]);

  // Stock - use variant stock if selected, otherwise product stock
  // For products with linked variants, don't show stock until variant is selected
  const hasLinkedVariants = variantLinks && variantLinks.length > 0;
  const allOptionsSelected = optionGroups.length > 0 && optionGroups.every(group => selectedOptions[group.id] != null);
  
  let stock, inStock;
  if (hasLinkedVariants && optionGroups.length > 0) {
    // For linked variants, only show stock after all options are selected
    if (allOptionsSelected && selectedVariant) {
      stock = selectedVariant.stock;
      inStock = stock > 0;
    } else {
      // Don't show stock status yet - wait for selection
      stock = null;
      inStock = null; // null means "not determined yet"
    }
  } else {
    // Regular products or products without linked variants
    stock = selectedVariant ? selectedVariant.stock : (dbProduct?.stock || 0);
    inStock = stock > 0;
  }

  // Find variant by selected options (for linking logic)
  const currentSourceVariant = useMemo(() => {
    if (variants.length === 0 || Object.keys(selectedOptions).length === 0) {
      return null;
    }
    // Find variant that matches all currently selected options
    return findVariantByOptions(variants, selectedOptions);
  }, [variants, selectedOptions]);

  // Get linked target variants for the current source variant
  const linkedTargetVariants = useMemo(() => {
    if (!currentSourceVariant || !variantLinks || variantLinks.length === 0) {
      return null; // No links, show all variants
    }
    const targetLinks = variantLinks.filter(link => link.source_variant_id === currentSourceVariant.id);
    const targetVariantIds = targetLinks.map(link => link.target_variant_id);
    return variants.filter(v => targetVariantIds.includes(v.id));
  }, [currentSourceVariant, variantLinks, variants]);

  // Check if all custom fields are filled
  const allCustomFieldsFilled = useMemo(() => {
    if (!customFields || customFields.length === 0) {
      return true; // No custom fields, so they're all "filled"
    }
    
    return customFields.every(field => {
      const fieldValue = customFieldValues[field.id];
      const isEmpty = fieldValue === undefined || 
                     fieldValue === '' || 
                     fieldValue === null ||
                     (Array.isArray(fieldValue) && fieldValue.length === 0) ||
                     (field.field_type === 'checkbox' && !fieldValue);
      
      return !isEmpty;
    });
  }, [customFields, customFieldValues]);

  // Get available option values for a group based on linked variants and selected options
  const getAvailableValuesForGroup = (group, currentGroupIndex) => {
    // If this is the first group, show all available values that have variants
    if (currentGroupIndex === 0) {
      return group.values.filter(value => {
        return variants.some(v =>
          v.variant_options?.some(vo => vo.value_id === value.id && vo.group_id === group.id) &&
          v.stock > 0
        );
      });
    }

    // For subsequent groups, filter based on previously selected options
    // First, find all variants that match the currently selected options from previous groups
    const previousSelections = {};
    for (let i = 0; i < currentGroupIndex; i++) {
      const prevGroup = optionGroups[i];
      if (selectedOptions[prevGroup.id]) {
        previousSelections[prevGroup.id] = selectedOptions[prevGroup.id];
      }
    }

    // If no previous selections, show all available values
    if (Object.keys(previousSelections).length === 0) {
      return group.values.filter(value => {
        return variants.some(v =>
          v.variant_options?.some(vo => vo.value_id === value.id && vo.group_id === group.id) &&
          v.stock > 0
        );
      });
    }

    // Find variants that match all previous selections
    const matchingVariants = variants.filter(variant => {
      if (!variant.variant_options || variant.variant_options.length === 0) {
        return false;
      }
      
      // Check if variant matches all previous selections
      return Object.entries(previousSelections).every(([groupId, valueId]) => {
        return variant.variant_options.some(vo => 
          vo.group_id === groupId && vo.value_id === valueId
        );
      });
    });

    // If variant links exist and we have a source variant, use linked variants
    if (linkedTargetVariants && linkedTargetVariants.length > 0) {
      // Filter to only show values that exist in linked target variants
      return group.values.filter(value => {
        return linkedTargetVariants.some(v =>
          v.variant_options?.some(vo => vo.value_id === value.id && vo.group_id === group.id) &&
          v.stock > 0
        );
      });
    }

    // Otherwise, show values that exist in variants matching previous selections
    return group.values.filter(value => {
      return matchingVariants.some(v =>
        v.variant_options?.some(vo => vo.value_id === value.id && vo.group_id === group.id) &&
        v.stock > 0
      );
    });
  };

  // Handle option selection
  const handleOptionSelect = (groupId, valueId) => {
    setSelectedOptions(prev => {
      const newOptions = { ...prev, [groupId]: valueId };
      
      // If selecting an option from the first group, clear subsequent selections
      // to ensure we're working with the correct linked variants
      const groupIndex = optionGroups.findIndex(g => g.id === groupId);
      if (groupIndex === 0) {
        // Clear all selections after the first group
        const clearedOptions = { [groupId]: valueId };
        return clearedOptions;
      }
      
      return newOptions;
    });
  };

  // Reset selected image to first variant image when variant changes
  useEffect(() => {
    // When variant changes, reset to first image
    // Since variant images are placed first in the images array, index 0 will be the variant's first image
    setSelectedImage(0);
  }, [selectedVariant?.id]);
  
  // Also update when images array changes to ensure we're showing the correct image
  useEffect(() => {
    // If current selected image index is out of bounds, reset to 0
    if (selectedImage >= images.length && images.length > 0) {
      setSelectedImage(0);
    }
  }, [images.length, selectedImage]);

  // Actual add to cart logic (without session check)
  const performAddToCart = async () => {
    if (!dbProduct) return;

    // If product has option groups, require all options to be selected
    if (optionGroups.length > 0) {
      if (!allOptionsSelected) {
        const missingGroups = optionGroups
          .filter(group => !selectedOptions[group.id])
          .map(group => group.label || group.name)
          .join(', ');
        showToast(
          isArabic 
            ? `يرجى اختيار: ${missingGroups}`
            : `Please select: ${missingGroups}`,
          'error'
        );
        return;
      }

      // If variants exist, try to find matching variant
      if (variants.length > 0) {
        if (!selectedVariant) {
          // If no exact variant match found, show error
          showToast(
            isArabic 
              ? 'المتغير المحدد غير موجود. يرجى اختيار خيارات صحيحة.'
              : 'Selected variant not found. Please select valid options.',
            'error'
          );
          return;
        }
        
        // Check if selected variant has stock
        if (selectedVariant.stock < quantity) {
          showToast(
            isArabic
              ? `المخزون المتاح: ${selectedVariant.stock} فقط`
              : `Only ${selectedVariant.stock} items available in stock`,
            'error'
          );
          return;
        }
      }
    } else {
      // For products without variants, check product stock
      if (dbProduct.stock < quantity) {
        showToast(
          isArabic
            ? `المخزون المتاح: ${dbProduct.stock} فقط`
            : `Only ${dbProduct.stock} items available in stock`,
          'error'
        );
        return;
      }
    }

    // Validate custom fields - all are mandatory
    if (customFields && customFields.length > 0) {
      const missingFields = [];
      customFields.forEach(field => {
        const fieldValue = customFieldValues[field.id];
        const isEmpty = fieldValue === undefined || 
                       fieldValue === '' || 
                       fieldValue === null ||
                       (Array.isArray(fieldValue) && fieldValue.length === 0) ||
                       (field.field_type === 'checkbox' && !fieldValue);
        
        if (isEmpty) {
          missingFields.push((isArabic && field.label_ar) ? field.label_ar : (field.label || field.key_name.replace(/_/g, ' ')));
        }
      });

      if (missingFields.length > 0) {
        showToast(
          isArabic 
            ? `يرجى ملء الحقول المطلوبة: ${missingFields.join(', ')}`
            : `Please fill the required fields: ${missingFields.join(', ')}`,
          'error'
        );
        return;
      }
    }

    setAddingToCart(true);
    try {
      const variantId = selectedVariant ? selectedVariant.id : null;
      // Prepare custom fields data to store
      const customFieldsData = customFields && customFields.length > 0 ? 
        customFields.reduce((acc, field) => {
          const fieldValue = customFieldValues[field.id];
          if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
            acc[field.key_name || field.id] = fieldValue;
          }
          return acc;
        }, {}) : null;
      
      await addToCart(dbProduct.id, quantity, variantId, note.trim() || null, customFieldsData);
      console.log('Added to cart successfully', variantId ? `with variant ${variantId}` : '');
      showToast('Item added to cart successfully!', 'success');
      setNote(''); // Clear note after successful add
      // Clear custom field values after adding to cart
      if (customFields && customFields.length > 0) {
        setCustomFieldValues({});
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      // Only show error if it's not a login error (shouldn't happen now, but just in case)
      if (error?.message && error.message.includes('must be logged in')) {
        setShowLoginModal(true);
      } else {
        showToast(`Failed to add to cart: ${error?.message || 'Please try again'}`, 'error');
      }
    } finally {
      setAddingToCart(false);
    }
  };

  // Handle add to cart with session check
  const handleAddToCart = async () => {
    if (!dbProduct) return;

    // Check if user is logged in
    if (!session) {
      setShowLoginModal(true);
      return;
    }

    await performAddToCart();
  };

  // Wishlist toggle handler
  const handleWishlist = async () => {
    if (!session) {
      navigate('/login');
      return;
    }
    if (!dbProduct) return;

    if (wishlistAdding) return;
    setWishlistAdding(true);
    try {
      const fav = isFavorite(dbProduct.id);
      if (fav) {
        const res = await removeFavorite(dbProduct.id);
        if (res && res.error) throw res.error;
        showToast('Removed from wishlist', 'success');
      } else {
        const res = await addFavorite(dbProduct.id);
        if (res && res.error) throw res.error;
        showToast('Added to wishlist', 'success');
      }
    } catch (error) {
      console.error('Wishlist error:', error);
      const message =
        (error && (error.message || error.error || error.msg)) ||
        (typeof error === 'string' ? error : null) ||
        JSON.stringify(error) ||
        'Failed to update wishlist';
      showToast(message, 'error');
    } finally {
      setWishlistAdding(false);
    }
  };

  // Build product object for display (must be before early returns for hooks rule)
  const product = useMemo(() => {
    if (!dbProduct) return null;

    // Display name based on language
    const displayName = isArabic ? (dbProduct.name_ar || dbProduct.name) : dbProduct.name;

    return {
      id: dbProduct.id,
      title: displayName,
      price: currentPrice,
      originalPrice: discount ? originalPrice : null,
      discount,
      currency: dbProduct.currency || 'OMR',
      rating: 4.5,
      reviews: 0,
      inStock,
      stock,
      images,
      seller: { id: brand?.id, name: brand?.name || 'Unknown Brand' },
      description: dbProduct.description || 'No description available.',
      attributes: dbProduct.attributes || {},
    };
  }, [dbProduct, currentPrice, originalPrice, discount, inStock, stock, images, brand, isArabic]);

  // Redirect to home if product not found after loading
  useEffect(() => {
    if (!productsLoading && !dbProduct) {
      console.log('Product not found, redirecting to home');
      navigate('/');
    }
  }, [dbProduct, productsLoading, navigate]);

  if (productsLoading || optionsLoading || variantsLoading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '80px 20px', color: '#6b7280' }}>
          {isArabic ? 'جاري تحميل المنتج...' : 'Loading product...'}
        </div>
      </Container>
    );
  }

  if (!dbProduct || !product) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '80px 20px', color: '#6b7280' }}>
          {isArabic ? 'المنتج غير موجود' : 'Product not found'}
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <ProductContent>
        <ImageSection>
          <MainImage src={product.images[selectedImage]} alt={product.title} />
          <Thumbnails>
            {product.images.map((img, idx) => (
              <Thumbnail
                key={idx}
                src={img}
                active={idx === selectedImage}
                onClick={() => setSelectedImage(idx)}
                alt={`${product.title} ${idx + 1}`}
              />
            ))}
          </Thumbnails>
        </ImageSection>

        <DetailsSection>
          {/* Category Tag */}
          {dbProduct?.category && (
            <CategoryTag to={`/categories/${slugify(dbProduct.category.name_en || dbProduct.category.name_ar || 'category')}`}>
              {isArabic 
                ? (dbProduct.category.name_ar || dbProduct.category.name_en || 'Category')
                : (dbProduct.category.name_en || dbProduct.category.name_ar || 'Category')}
            </CategoryTag>
          )}
          
          {/* Product Title */}
          <Title>{product.title}</Title>

          {/* Price */}
          <PriceContainer>
            <CurrentPrice>
              {typeof currentPrice === 'number' ? currentPrice.toFixed(1) : currentPrice} {product.currency}
            </CurrentPrice>
            {discount && originalPrice && (
              <>
                <OriginalPrice>{originalPrice.toFixed(2)} {product.currency}</OriginalPrice>
                <Discount>-{discount}%</Discount>
              </>
            )}
          </PriceContainer>

          {/* Seller Info */}
          {brand && (
            <SellerText>
              {isArabic ? 'البائع:' : 'SELLER:'} <SellerLink to={`/${slugify(brand.name)}`}>{product.seller.name}</SellerLink>
            </SellerText>
          )}

          {/* Rating */}
          <Rating style={{ marginTop: '24px' }}>
            <Stars>
              {(() => {
                const filled = Math.floor(averageRating);
                const hasHalf = averageRating % 1 >= 0.5;
                const empty = 5 - filled - (hasHalf ? 1 : 0);
                return (
                  <>
                    {Array.from({ length: filled }).map((_, i) => (
                      <span key={`filled-${i}`} style={{ color: '#fbbf24' }}>★</span>
                    ))}
                    {hasHalf && <span style={{ color: '#fbbf24' }}>½</span>}
                    {Array.from({ length: empty }).map((_, i) => (
                      <span key={`empty-${i}`} style={{ color: '#d1d5db' }}>★</span>
                    ))}
                  </>
                );
              })()}
            </Stars>
            <RatingText>
              {averageRating.toFixed(1) || '0.0'}
              ({approvedRatings.filter(r => r.status === 'approved').length || 0} {isArabic ? (approvedRatings.filter(r => r.status === 'approved').length === 1 ? 'تقييم' : 'تقييمات') : (approvedRatings.filter(r => r.status === 'approved').length === 1 ? 'review' : 'reviews')})
            </RatingText>
          </Rating>

          {selectedVariant && (
            <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '16px', padding: '8px 12px', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #d1fae5' }}>
              <strong>{isArabic ? 'المتغير المحدد:' : 'Selected Variant:'}</strong> {selectedVariant.sku || (isArabic ? 'لا يوجد رمز' : 'No SKU')}
              {selectedVariant.variant_options && selectedVariant.variant_options.length > 0 && (
                <span style={{ marginLeft: '8px', color: '#065f46' }}>
                  ({selectedVariant.variant_options.map(vo =>
                    `${vo.option_group?.label || vo.option_group?.name}: ${vo.option_value?.value}`
                  ).join(', ')})
                </span>
              )}
            </div>
          )}

          {/* Product Options/Variants */}
          {optionGroups.length > 0 && !optionsLoading && (
            <Variants>
              {optionGroups.map((group, groupIndex) => {
                // Get available values for this group based on previous selections
                const availableValues = getAvailableValuesForGroup(group, groupIndex);
                
                return (
                  <div key={group.id} style={{ marginBottom: '24px' }}>
                    <VariantLabel>
                      {group.label || group.name}:
                    </VariantLabel>
                    <VariantOptions>
                      {availableValues.map(value => {
                        const isSelected = selectedOptions[group.id] === value.id;

                        return (
                          <VariantButton
                            key={value.id}
                            selected={isSelected}
                            disabled={false} // All values in availableValues are already filtered to be available
                            onClick={() => handleOptionSelect(group.id, value.id)}
                          >
                            {value.value}
                          </VariantButton>
                        );
                      })}
                    </VariantOptions>
                  </div>
                );
              })}
            </Variants>
          )}

          {inStock !== null && (
            <StockBadge style={{
              background: inStock ? '#d1fae5' : '#fee2e2',
              color: inStock ? '#065f46' : '#991b1b',
              marginTop: '16px',
              marginBottom: '16px'
            }}>
              <FiPackage /> {inStock ? (isArabic ? `متوفر في المخزون (${stock} متاح)` : `In Stock (${stock} available)`) : (isArabic ? 'غير متوفر' : 'Out of Stock')}
              {selectedVariant && (
                <span style={{ marginLeft: '8px', fontSize: '12px', opacity: 0.8 }}>
                  {isArabic ? '(مخزون المتغير)' : '(Variant stock)'}
                </span>
              )}
            </StockBadge>
          )}
          {inStock === null && hasLinkedVariants && optionGroups.length > 0 && (
            <StockBadge style={{
              background: '#fef3c7',
              color: '#92400e',
              marginTop: '16px',
              marginBottom: '16px'
            }}>
              <FiPackage /> {isArabic ? 'يرجى اختيار الخيارات للتحقق من المخزون' : 'Please select options to check stock availability'}
            </StockBadge>
          )}

          <QuantityControl>
            <span style={{ fontWeight: 500 }}>{isArabic ? 'الكمية:' : 'Quantity:'}</span>
            <QuantityWrapper>
              <QuantityButton
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={inStock === false || (hasLinkedVariants && inStock === null)}
              >
                <FiMinus />
              </QuantityButton>
              <Quantity>{quantity}</Quantity>
              <QuantityButton
                onClick={() => setQuantity(Math.min(stock || 1, quantity + 1))}
                disabled={inStock === false || (hasLinkedVariants && inStock === null)}
              >
                <FiPlus />
              </QuantityButton>
            </QuantityWrapper>
          </QuantityControl>

          {/* Custom Fields */}
          {customFields && customFields.length > 0 && (
            <div style={{ marginTop: '24px', marginBottom: '24px' }}>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#1f2937', marginBottom: '16px' }}>
                {isArabic ? 'معلومات إضافية' : 'Additional Information'}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {customFields
                  .sort((a, b) => (a.position || 0) - (b.position || 0))
                  .map(field => {
                    const fieldId = field.id;
                    // Always start empty - custom fields are mandatory and should be filled by user
                    const fieldValue = customFieldValues[fieldId] !== undefined ? customFieldValues[fieldId] : '';
                    
                    // Parse options if it's a JSON string
                    let parsedOptions = null;
                    if (field.options) {
                      try {
                        parsedOptions = typeof field.options === 'string' ? JSON.parse(field.options) : field.options;
                      } catch (e) {
                        parsedOptions = Array.isArray(field.options) ? field.options : null;
                      }
                    }
                    
                    // All custom fields are mandatory
                    const isRequired = true;
                    
                    return (
                      <div key={field.id} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                          {(isArabic && field.label_ar) ? field.label_ar : (field.label || field.key_name.replace(/_/g, ' '))}
                          <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>
                        </label>
                        {field.help_text && (
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                            {field.help_text}
                          </div>
                        )}
                        
                        {/* Render field based on type */}
                        {field.field_type === 'text' && (
                          <input
                            type="text"
                            value={fieldValue}
                            onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [fieldId]: e.target.value }))}
                            placeholder={field.placeholder || ''}
                            required={isRequired}
                            minLength={field.min_length}
                            maxLength={field.max_length}
                            pattern={field.pattern}
                            style={{
                              padding: '12px',
                              border: '2px solid #e5e7eb',
                              borderRadius: '8px',
                              fontSize: '14px',
                              transition: 'border-color 0.2s'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#5efba9'}
                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                          />
                        )}
                        
                        {field.field_type === 'textarea' && (
                          <textarea
                            value={fieldValue}
                            onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [fieldId]: e.target.value }))}
                            placeholder={field.placeholder || ''}
                            required={isRequired}
                            minLength={field.min_length}
                            maxLength={field.max_length}
                            rows={4}
                            style={{
                              padding: '12px',
                              border: '2px solid #e5e7eb',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              resize: 'vertical',
                              transition: 'border-color 0.2s'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#5efba9'}
                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                          />
                        )}
                        
                        {field.field_type === 'number' && (
                          <input
                            type="number"
                            value={fieldValue}
                            onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [fieldId]: e.target.value }))}
                            placeholder={field.placeholder || ''}
                            required={isRequired}
                            min={field.min_value}
                            max={field.max_value}
                            step="0.01"
                            style={{
                              padding: '12px',
                              border: '2px solid #e5e7eb',
                              borderRadius: '8px',
                              fontSize: '14px',
                              transition: 'border-color 0.2s'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#5efba9'}
                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                          />
                        )}
                        
                        {field.field_type === 'email' && (
                          <input
                            type="email"
                            value={fieldValue}
                            onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [fieldId]: e.target.value }))}
                            placeholder={field.placeholder || 'example@email.com'}
                            required={field.required}
                            style={{
                              padding: '12px',
                              border: '2px solid #e5e7eb',
                              borderRadius: '8px',
                              fontSize: '14px',
                              transition: 'border-color 0.2s'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#5efba9'}
                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                          />
                        )}
                        
                        {field.field_type === 'phone' && (
                          <input
                            type="tel"
                            value={fieldValue}
                            onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [fieldId]: e.target.value }))}
                            placeholder={field.placeholder || '+968 XXXX XXXX'}
                            required={field.required}
                            style={{
                              padding: '12px',
                              border: '2px solid #e5e7eb',
                              borderRadius: '8px',
                              fontSize: '14px',
                              transition: 'border-color 0.2s'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#5efba9'}
                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                          />
                        )}
                        
                        {field.field_type === 'date' && (
                          <input
                            type="date"
                            value={fieldValue}
                            onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [fieldId]: e.target.value }))}
                            required={field.required}
                            style={{
                              padding: '12px',
                              border: '2px solid #e5e7eb',
                              borderRadius: '8px',
                              fontSize: '14px',
                              transition: 'border-color 0.2s'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#5efba9'}
                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                          />
                        )}
                        
                        {field.field_type === 'checkbox' && (
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={fieldValue === true || fieldValue === 'true'}
                              onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [fieldId]: e.target.checked }))}
                              required={isRequired}
                              style={{ width: '18px', height: '18px', accentColor: '#5efba9', cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '14px', color: '#374151' }}>
                              {field.placeholder || field.label || 'I agree'}
                            </span>
                          </label>
                        )}
                        
                        {(field.field_type === 'select' || field.field_type === 'radio') && parsedOptions && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {Array.isArray(parsedOptions) ? parsedOptions.map((option, idx) => (
                              <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                  type={field.field_type === 'radio' ? 'radio' : 'checkbox'}
                                  name={field.field_type === 'radio' ? `field_${fieldId}` : undefined}
                                  value={typeof option === 'object' ? option.value : option}
                                  checked={field.field_type === 'radio' 
                                    ? fieldValue === (typeof option === 'object' ? option.value : option)
                                    : Array.isArray(fieldValue) && fieldValue.includes(typeof option === 'object' ? option.value : option)
                                  }
                                  onChange={(e) => {
                                    if (field.field_type === 'radio') {
                                      setCustomFieldValues(prev => ({ ...prev, [fieldId]: e.target.value }));
                                    } else {
                                      const currentValues = Array.isArray(fieldValue) ? fieldValue : [];
                                      const newValues = e.target.checked
                                        ? [...currentValues, e.target.value]
                                        : currentValues.filter(v => v !== e.target.value);
                                      setCustomFieldValues(prev => ({ ...prev, [fieldId]: newValues }));
                                    }
                                  }}
                                  required={isRequired && idx === 0}
                                  style={{ width: '18px', height: '18px', accentColor: '#5efba9', cursor: 'pointer' }}
                                />
                                <span style={{ fontSize: '14px', color: '#374151' }}>
                                  {typeof option === 'object' ? option.label : option}
                                </span>
                              </label>
                            )) : null}
                          </div>
                        )}
                        
                        {field.field_type === 'select' && parsedOptions && (
                          <select
                            value={fieldValue}
                            onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [fieldId]: e.target.value }))}
                            required={isRequired}
                            style={{
                              padding: '12px',
                              border: '2px solid #e5e7eb',
                              borderRadius: '8px',
                              fontSize: '14px',
                              background: 'white',
                              cursor: 'pointer',
                              transition: 'border-color 0.2s'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#5efba9'}
                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                          >
                            <option value="">{isArabic ? 'اختر...' : 'Select...'}</option>
                            {Array.isArray(parsedOptions) && parsedOptions.map((option, idx) => (
                              <option key={idx} value={typeof option === 'object' ? option.value : option}>
                                {typeof option === 'object' ? option.label : option}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          <div>
            <NoteLabel>{isArabic ? 'ملاحظة (اختياري)' : 'Note (Optional)'}</NoteLabel>
            <NoteInput
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={isArabic ? 'أضف ملاحظة خاصة لهذا المنتج...' : 'Add a special note for this product...'}
              maxLength={500}
            />
            {note.length > 0 && (
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                {note.length}/500
              </div>
            )}
          </div>

          <ActionButtons>
            <PrimaryButton
              onClick={handleAddToCart}
              disabled={
                (inStock === false) || 
                addingToCart || 
                (optionGroups.length > 0 && !allOptionsSelected) || // Require all options to be selected
                (hasLinkedVariants && inStock === null) || // For linked variants, wait for stock determination
                (customFields && customFields.length > 0 && !allCustomFieldsFilled) // Require all custom fields to be filled
              }
            >
              <FiShoppingCart />
              {addingToCart ? (isArabic ? 'جاري الإضافة...' : 'Adding...') : 
               optionGroups.length > 0 && !allOptionsSelected ? (isArabic ? 'اختر الخيارات' : 'Select Options') : 
               hasLinkedVariants && inStock === null ? (isArabic ? 'اختر الخيارات أولاً' : 'Select Options First') :
               customFields && customFields.length > 0 && !allCustomFieldsFilled ? (isArabic ? 'يرجى ملء المعلومات الإضافية' : 'Please fill Additional Information') :
               (isArabic ? 'أضف إلى السلة' : 'Add to Cart')}
            </PrimaryButton>

            {/* Wishlist button wired to useFavorites */}
            <SecondaryButton onClick={handleWishlist} disabled={wishlistAdding}>
              <FiHeart /> {wishlistAdding ? (isArabic ? 'جاري الحفظ...' : 'Saving...') : (dbProduct && isFavorite(dbProduct.id) ? (isArabic ? 'في المفضلة' : 'In Wishlist') : (isArabic ? 'المفضلة' : 'Wishlist'))}
            </SecondaryButton>

          </ActionButtons>
        </DetailsSection>
      </ProductContent>

      <Description>
        <DescriptionTitle>{isArabic ? 'وصف المنتج' : 'Product Description'}</DescriptionTitle>
        <DescriptionText>{product.description}</DescriptionText>

      </Description>

      {/* Ratings Section */}
      <RatingsSection>
        <RatingsHeader>
          <RatingsTitle>{isArabic ? 'تقييمات العملاء' : 'Customer Reviews'}</RatingsTitle>
          <AverageRating>
            <AverageStars>
              {(() => {
                const filled = Math.floor(averageRating);
                const empty = 5 - filled;
                return (
                  <>
                    {Array.from({ length: filled }).map((_, i) => (
                      <FiStar key={`filled-${i}`} style={{ fill: '#fbbf24', color: '#fbbf24' }} />
                    ))}
                    {Array.from({ length: empty }).map((_, i) => (
                      <FiStar key={`empty-${i}`} style={{ color: '#d1d5db' }} />
                    ))}
                  </>
                );
              })()}
            </AverageStars>
            <AverageNumber>{averageRating.toFixed(1) || '0.0'}</AverageNumber>
            <RatingCount>({approvedRatings.filter(r => r.status === 'approved').length || 0} {isArabic ? (approvedRatings.filter(r => r.status === 'approved').length === 1 ? 'تقييم' : 'تقييمات') : (approvedRatings.filter(r => r.status === 'approved').length === 1 ? 'review' : 'reviews')})</RatingCount>
          </AverageRating>
        </RatingsHeader>

        {/* Rate Button - Only show if user can rate and order status is delivered */}
        {canRate && matchingOrderItem && matchingOrder && matchingOrder.status === 'delivered' && !canRateLoading && (
          <div style={{ marginBottom: '24px' }}>
            <RatingButton onClick={() => setShowRatingModal(true)}>
              <FiStar /> {existingRating ? (isArabic ? 'تعديل تقييمك' : 'Edit Your Review') : (isArabic ? 'قيّم هذا المنتج' : 'Rate This Product')}
            </RatingButton>
          </div>
        )}

        {/* Ratings List */}
        {ratingsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            {isArabic ? 'جاري تحميل التقييمات...' : 'Loading reviews...'}
          </div>
        ) : approvedRatings.length === 0 ? (
          <EmptyRatings>
            <p>{isArabic ? 'لا توجد تقييمات بعد. كن أول من يقيّم هذا المنتج!' : 'No reviews yet. Be the first to review this product!'}</p>
          </EmptyRatings>
        ) : (
          <div>
            {approvedRatings.map((rating) => (
              <RatingItem key={rating.id}>
                <RatingItemHeader>
                  <ReviewerInfo>
                    <ReviewerName>
                      {rating.created_name ||
                        'Anonymous'}
                      {rating.purchase_verified && (
                        <span style={{
                          marginLeft: '8px',
                          fontSize: '12px',
                          color: '#059669',
                          fontWeight: 'normal'
                        }}>
                          ✓ {isArabic ? 'شراء موثّق' : 'Verified Purchase'}
                        </span>
                      )}
                    </ReviewerName>
                    <ReviewDate>
                      {rating.created_at ? new Date(rating.created_at).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : (isArabic ? 'تاريخ غير معروف' : 'Unknown date')}
                    </ReviewDate>
                  </ReviewerInfo>
                  <RatingStars>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <FiStar
                        key={i}
                        style={{
                          fill: i < rating.stars ? '#fbbf24' : 'none',
                          color: i < rating.stars ? '#fbbf24' : '#d1d5db'
                        }}
                      />
                    ))}
                    <span style={{ marginLeft: '8px', fontSize: '14px', color: '#374151', fontWeight: '600' }}>
                      {rating.stars}/5
                    </span>
                  </RatingStars>
                </RatingItemHeader>
                {rating.title && (
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1f2937',
                    marginBottom: '8px',
                    marginTop: '8px'
                  }}>
                    {rating.title}
                  </div>
                )}
                {rating.body && (
                  <RatingBody>"{rating.body}"</RatingBody>
                )}
              </RatingItem>
            ))}
          </div>
        )}
      </RatingsSection>

      {/* Rating Modal */}
      {showRatingModal && matchingOrderItem && matchingOrder && (
        <RatingModal
          orderItem={matchingOrderItem}
          order={matchingOrder}
          existingRating={existingRating}
          onClose={() => setShowRatingModal(false)}
          onSubmitted={async () => {
            setShowRatingModal(false);
            // Refetch ratings immediately to show the updated review
            setTimeout(async () => {
              await refetchRatings();
            }, 300); // Small delay to ensure DB commit
          }}
        />
      )}

      {/* Login Modal */}
      {showLoginModal && (
        <LoginModal
          onClose={() => setShowLoginModal(false)}
        />
      )}

      {/* Render toast UI */}
      {toasts.length > 0 && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {toasts.map(t => (
            <div key={t.id} style={{
              background: t.type === 'success' ? '#f0fdf4' : '#fff5f5',
              border: `1px solid ${t.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
              padding: '10px 14px',
              borderRadius: 8,
              minWidth: 220,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12
            }}>
              <div style={{ color: t.type === 'success' ? '#065f46' : '#7f1d1d', fontWeight: 600 }}>{t.message}</div>
              <button onClick={() => removeToast(t.id)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit' }}>✕</button>
            </div>
          ))}
        </div>
      )}
    </Container>
  );
}

export default ProductDetail;
